import { ArrowPathIcon, ArrowTopRightOnSquareIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useMemo } from 'react';
import { Button } from '../../components/Button';
import { useReferralSaleStateTranslations } from '../../hooks/translations/useReferralRoleTranslations';
import { useFormatDate } from '../../hooks/useFormatDate';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { T, useT } from '../../translation/src';
import {
  ReferralDealKind,
  ReferralPayoutFieldsFragment,
  ReferralRole,
  ReferralSaleFieldsFragment,
  ReferralSaleState,
  useSuperadminCreateReferralPayoutMutation,
  useSuperadminReferralHistoryQuery,
  useSuperadminRefreshReferralsMutation,
} from '../../types/graphqlTypes';
import { formatPrice } from '../../utils/formatPrice';
import { ordersForCustomers } from '../organisation/referrals/partnerRoles';

const STATE_CLASS: Record<ReferralSaleState, string> = {
  [ReferralSaleState.Pending]: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  [ReferralSaleState.Payable]: 'bg-warning-yellow-500 text-black',
  [ReferralSaleState.PaidOut]: 'bg-eerie-black text-neon-green-300 dark:bg-black dark:text-white',
  [ReferralSaleState.Void]: 'bg-error-red-500 text-black',
};

const OdooLink: React.FC<{ url?: string | null; id?: number | null }> = ({ url, id }) =>
  url && id ? (
    <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1 underline whitespace-nowrap">
      #{id}
      <ArrowTopRightOnSquareIcon className="w-3 h-3" />
    </a>
  ) : (
    <span className="text-gray-400">—</span>
  );

const SaleRow: React.FC<{ sale: ReferralSaleFieldsFragment; payoutPaid: boolean }> = ({ sale, payoutPaid }) => {
  const { formatDay } = useFormatDate();
  const states = useReferralSaleStateTranslations();
  const paidLabel = useT('paid', { swc: true });
  const inPayoutLabel = useT('in payout', { swc: true });
  // Nothing is ever paid out to a customer referrer, nor for a cut that came off the invoice (a reseller's or
  // full-service partner's): the sale stays "payable", which there means paid. A sale in a payout is paid out
  // only once the payout's bill is paid.
  const settled = sale.state === ReferralSaleState.Payable && (sale.roleAtSale === ReferralRole.Customer || sale.deductedOnInvoice);
  const inPayout = sale.state === ReferralSaleState.PaidOut && !payoutPaid;
  const stateLabel = settled ? paidLabel : inPayout ? inPayoutLabel : states[sale.state];
  const stateClass = settled ? STATE_CLASS[ReferralSaleState.PaidOut] : inPayout ? STATE_CLASS[ReferralSaleState.Payable] : STATE_CLASS[sale.state];
  return (
    <div className="flex items-center gap-3 py-2 text-sm border-b border-gray-200 last:border-b-0 dark:border-gray-700">
      <span className="w-20 text-xs text-gray-500 shrink-0">{formatDay(sale.soldAt)}</span>
      <div className="flex flex-col min-w-0 grow">
        <span className="truncate">{sale.referredOrganisationName}</span>
        <span className="text-xs text-gray-500">
          {formatPrice(sale.amountCents)} × {(sale.cutBasisPoints / 100).toFixed(2)} %{sale.dealKind === ReferralDealKind.First ? ' · first' : ''}
          {sale.deductedOnInvoice ? ' · off the invoice' : ''}
          {sale.roleAtSale === ReferralRole.Customer && sale.rewardGrantId ? ' · +1 project' : ''}
          {sale.voidReason ? ` · ${sale.voidReason}` : ''}
        </span>
      </div>
      <span className="w-20 text-right shrink-0">{formatPrice(sale.cutCents)}</span>
      <span className={classNames('shrink-0 rounded-md px-2 py-0.5 text-xs', stateClass)}>{stateLabel}</span>
      <span className="w-16 text-xs shrink-0">
        <OdooLink url={sale.odooInvoiceUrl} id={sale.odooInvoiceId} />
      </span>
    </div>
  );
};

const PayoutRow: React.FC<{ payout: ReferralPayoutFieldsFragment }> = ({ payout }) => {
  const { formatDay } = useFormatDate();
  return (
    <div className="flex items-center gap-3 py-2 text-sm border-b border-gray-200 last:border-b-0 dark:border-gray-700">
      <span className="w-20 text-xs text-gray-500 shrink-0">{formatDay(payout.createdAt)}</span>
      <div className="flex flex-col min-w-0 grow">
        <span className="truncate">{payout.periodKey}</span>
        <span className="text-xs text-gray-500">
          <T _str="{count} sales" args={{ count: payout.saleCount }} />
          {payout.manual ? ' · ' : ''}
          {payout.manual && <T _str="by hand" />}
          {' · '}
          {payout.paidAt ? (
            <span className="text-neon-green-700 dark:text-neon-green-300">
              <T _str="bill paid" swc /> {formatDay(payout.paidAt)}
            </span>
          ) : (
            <T _str="bill not paid yet" swc />
          )}
        </span>
      </div>
      <span className="w-20 text-right shrink-0">{formatPrice(payout.totalCents)}</span>
      <span className="w-16 text-xs shrink-0">
        {payout.odooVendorBillId ? (
          <OdooLink url={payout.odooVendorBillUrl} id={payout.odooVendorBillId} />
        ) : (
          <span className="text-warning-yellow-700 dark:text-warning-yellow-500">
            <T _str="no bill yet" />
          </span>
        )}
      </span>
    </div>
  );
};

export interface ReferralHistoryPanelProps {
  organisationId: string;
  role: ReferralRole;
  // Whether the panel is on screen: it asks the server only then.
  active: boolean;
}
// Every sale attributed to the organisation and every payout made to it, each with its Odoo record, to
// check the numbers against Odoo; plus the two things a superadmin may do now rather than wait for the
// daily job: refresh, and pay out.
const ReferralHistoryPanel_: React.FC<ReferralHistoryPanelProps> = ({ organisationId, role, active }) => {
  const { data, loading } = useSuperadminReferralHistoryQuery({ variables: { organisationId }, skip: !active, fetchPolicy: 'cache-and-network' });
  const [refresh, { loading: refreshing }] = useSuperadminRefreshReferralsMutation({ refetchQueries: ['superadminReferralHistory'] });
  const [payOut, { loading: payingOut }] = useSuperadminCreateReferralPayoutMutation({ refetchQueries: ['superadminReferralHistory'] });
  const { showToast } = useToastNotifications();
  const refreshFailed = useT('could not refresh the referrals', { swc: true });
  const payoutFailed = useT('could not pay out', { swc: true });
  const nothingToPay = useT('nothing payable: no payout was made', { swc: true });
  const paidOut = useT('payout made', { swc: true });
  const sales = useMemo(() => data?.superadminReferralHistory.sales ?? [], [data]);
  const payouts = useMemo(() => data?.superadminReferralHistory.payouts ?? [], [data]);
  const payable = useMemo(() => sales.filter((sale) => sale.state === ReferralSaleState.Payable && !sale.deductedOnInvoice).reduce((sum, sale) => sum + sale.cutCents, 0), [sales]);
  const paidPayoutIds = useMemo(() => new Set(payouts.filter((payout) => payout.paidAt).map((payout) => payout.id)), [payouts]);
  // Pay out only what can be: a customer referrer is never paid out, and a reseller's or full-service partner's discount
  // comes off its invoices — what it still has payable is from before (a lead generator's commission).
  const canPayOut = role !== ReferralRole.Customer && payable > 0;
  const invoiced = ordersForCustomers(role);

  const onRefresh = useCallback(async () => {
    try {
      await refresh({ variables: { organisationId } });
    } catch (e) {
      showToast({ title: refreshFailed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
    }
  }, [organisationId, refresh, refreshFailed, showToast]);

  const onPayOut = useCallback(async () => {
    try {
      const result = await payOut({ variables: { organisationId } });
      const payout = result.data?.superadminCreateReferralPayout;
      showToast({ title: payout ? paidOut : nothingToPay, text: payout ? formatPrice(payout.totalCents) : '', type: payout ? ToastType.SUCCESS : ToastType.WARNING });
    } catch (e) {
      showToast({ title: payoutFailed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
    }
  }, [nothingToPay, organisationId, paidOut, payOut, payoutFailed, showToast]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="text-gray-500">
          {(!invoiced || payable > 0) && (
            <>
              <T _str="payable now" swc />: {formatPrice(payable)}
            </>
          )}
        </span>
        <div className="flex gap-2">
          <Button variant="secondary" size="xs" onClick={onRefresh} loading={refreshing}>
            <ArrowPathIcon className="w-4 h-4 mr-1" />
            <T _str="refresh now" swc />
          </Button>
          {canPayOut && (
            <Button variant="primary" size="xs" onClick={onPayOut} loading={payingOut}>
              <BanknotesIcon className="w-4 h-4 mr-1" />
              <T _str="pay out now" swc />
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <span className="mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">
          {invoiced ? <T _str="orders" swc /> : <T _str="sales" swc />} ({sales.length})
        </span>
        <div className="flex flex-col pr-2 overflow-y-auto max-h-72">
          {!loading && sales.length === 0 && <div className="py-4 text-sm text-center text-gray-500">{invoiced ? <T _str="no orders attributed yet" swc /> : <T _str="no sales attributed yet" swc />}</div>}
          {sales.map((sale) => (
            <SaleRow key={sale.id} sale={sale} payoutPaid={!!sale.payoutId && paidPayoutIds.has(sale.payoutId)} />
          ))}
        </div>
      </div>
      {/* A partner invoiced for its customers has payouts only from before (a lead generator's commission). */}
      {(!invoiced || payouts.length > 0) && (
        <div className="flex flex-col">
          <span className="mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">
            <T _str="payouts" swc /> ({payouts.length})
          </span>
          <div className="flex flex-col pr-2 overflow-y-auto max-h-48">
            {!loading && payouts.length === 0 && (
              <div className="py-4 text-sm text-center text-gray-500">
                <T _str="no payouts yet" swc />
              </div>
            )}
            {payouts.map((payout) => (
              <PayoutRow key={payout.id} payout={payout} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
export const ReferralHistoryPanel = memo(ReferralHistoryPanel_);
